import type {DataRouterFileOverviewPayload, DataRouterFileTreePagePayload} from '@github-ui/code-view-types'
import {getAppTypeHeader} from '@github-ui/fetch-headers'
import {repositoryTreePath} from '@github-ui/paths'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import {type JsonResponse, mainQuery} from '@github-ui/react-core/future/main-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'

import {codeViewAppBuilder} from '../config/app-builder'
import {CodeViewPathIsBlobError} from '../pages/CodeViewTreeErrorBoundary'
import {fileTreeQuery} from '../queries/file-tree-query'
import {latestCommitQuery} from '../queries/latest-commit-query'
import {treeCommitInfoQuery} from '../queries/tree-commit-info-query'
import type {CodeViewPathParams} from '../queries/types/shared-query-types'
import {codeViewBlobRoute} from './code-view-blob-route'
import {shouldParseCodeViewErrorResponse} from './code-view-error-response'

function throwError(json: JsonResponse, routeId: string): never {
  if (json.payload?.[codeViewBlobRoute.id]) {
    throw new CodeViewPathIsBlobError()
  }

  throw new Error(`Unable to find payload for id: ${routeId}`)
}

export function buildCodeViewTreeRoute(appBuilder: DataRouterApplicationBuilder<string>) {
  return appBuilder.createQueryRouteConfig('codeViewTreeRoute', {
    path: '/:owner/:repo/tree/:branch/*',
    queries: [
      mainQuery<DataRouterFileOverviewPayload | DataRouterFileTreePagePayload>({
        queryDeps: ({params: routeParams, searchParams}) => {
          // React Router and TanStack Router have different pathname encoding contracts.
          // Rebuild from decoded route params so each segment is encoded exactly once.
          const params = routeParams as CodeViewPathParams
          const encodedPathname = repositoryTreePath({
            repo: {ownerLogin: params.owner, name: params.repo},
            commitish: params.branch,
            action: 'tree',
            path: params['*'],
          })
          return {
            pathname: encodedPathname,
            searchParams: {
              search: searchParams.get('search'),
              files: searchParams.get('files'),
            },
          }
        },

        queryFn: async ({routeId, queryDeps}) => {
          // Merge the appTypeHeader into any existing headers in queryDeps.init, if present
          const appTypeHeader = getAppTypeHeader('dataRouter')
          const mergedQueryDeps = {
            ...queryDeps,
            init: {
              ...queryDeps?.init,
              headers: {...appTypeHeader, ...queryDeps?.init?.headers},
            },
          }

          const json = await queryFnFetch<JsonResponse>(
            {
              queryDeps: mergedQueryDeps,
            },
            {
              shouldParseErrorResponse: shouldParseCodeViewErrorResponse,
            },
          )

          const routePayload = json.payload?.[routeId]
          if (!routePayload) {
            throwError(json, routeId)
          }

          return {
            meta: json.meta,
            payload: routePayload,
          } as unknown as DataRouterFileOverviewPayload | DataRouterFileTreePagePayload // A select will run internally to extract the payload correctly.
        },
      }),
      fileTreeQuery,
      latestCommitQuery,
      treeCommitInfoQuery,
    ],
    uiServiceOptions: {
      tsrConfig: {
        validateSearch: (search: Record<string, unknown>): {search?: string} => search as {search?: string},
      },
    },
  })
}

export const codeViewTreeRoute = buildCodeViewTreeRoute(codeViewAppBuilder)
